"use client";

import React from "react";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { HoverCardTrigger } from "@/components/ui/hover-card";
import {
   InlineCitationCard,
   InlineCitationCardBody,
} from "@/components/ai-elements/inline-citation";
import { useTranslation } from "react-i18next";
import { useDocumentPreviewContext } from "@/hooks/useDocumentPreview";
import { getDomain } from "@/utils/url";
import type { SourceRef } from "@/types/ask";

interface CitationGroupPillProps {
   nums: string;
   sources?: SourceRef[];
}

const CitationGroupPill: React.FC<CitationGroupPillProps> = ({ nums, sources }) => {
   const [page, setPage] = React.useState(0);
   const openDocument = useDocumentPreviewContext();
   const { t } = useTranslation("ask");

   const indices = nums
      .split(",")
      .map((n) => Number.parseInt(n, 10) - 1)
      .filter((i) => i >= 0 && (sources?.[i]?.url || sources?.[i]?.id));

   const resolved = indices.map((i) => sources![i]);

   if (resolved.length === 0) return null;

   const safePage = Math.min(page, resolved.length - 1);
   const source = resolved[safePage];
   const isWeb = !!source.url;

   const triggerLabel = isWeb ? getDomain(source.url!) : source.fileName ?? t("document");

   const handleClick = () => {
      if (isWeb) {
         window.open(source.url, "_blank", "noopener,noreferrer");
      } else if (source.id) {
         openDocument({
            id: source.id,
            fileName: source.fileName,
            pages: source.pages,
         });
      }
   };

   return (
      <InlineCitationCard>
         <HoverCardTrigger asChild>
            <button
               type="button"
               className="ml-1 inline-flex h-auto cursor-pointer items-center rounded-lg border border-gpt-border bg-gpt-card px-2 py-0.5 align-baseline font-medium text-gpt-text-secondary text-[11px] transition-all duration-150 hover:border-gpt-accent hover:bg-gpt-accent-light hover:text-gpt-text-primary"
               onClick={handleClick}
            >
               {!isWeb && <Icon icon="file" size={ICON_SIZES.XS} className="mr-1 shrink-0" />}
               {triggerLabel}
               {resolved.length > 1 && <span className="ml-0.5 opacity-60">+{resolved.length - 1}</span>}
            </button>
         </HoverCardTrigger>
         <InlineCitationCardBody>
            <span
               className="flex items-start gap-1 p-3 hover:bg-gpt-card transition-colors cursor-pointer"
               role="button"
               tabIndex={0}
               onClick={handleClick}
               onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                     e.preventDefault();
                     handleClick();
                  }
               }}
            >
               {resolved.length > 1 && (
                  <button
                     type="button"
                     aria-label={t("previousSource")}
                     className="shrink-0 p-1.5 -m-1 rounded text-gpt-text-secondary transition-colors hover:text-gpt-text-primary hover:bg-gpt-card disabled:opacity-30"
                     disabled={safePage === 0}
                     onClick={(e) => {
                        e.stopPropagation();
                        setPage((p) => p - 1);
                     }}
                  >
                     <Icon icon="chevron-left" className="text-sm" />
                  </button>
               )}
               <span className="min-w-0 flex-1 space-y-1">
                  <span className="line-clamp-2 block font-medium text-gpt-text-primary text-sm leading-tight">
                     {source.fileName ?? source.url ?? t("document")}
                  </span>
                  {isWeb && source.url && (
                     <span className="block truncate break-all text-gpt-text-secondary text-xs">
                        {getDomain(source.url)}
                     </span>
                  )}
                  {!isWeb && source.pages && source.pages.length > 0 && (
                     <span className="block text-gpt-text-secondary text-xs">
                        {t("pages")}: {source.pages.join(", ")}
                     </span>
                  )}
               </span>
               {resolved.length > 1 && (
                  <span className="flex shrink-0 flex-col items-center gap-1">
                     <button
                        type="button"
                        aria-label={t("nextSource")}
                        className="p-1.5 -m-1 rounded text-gpt-text-secondary transition-colors hover:text-gpt-text-primary hover:bg-gpt-card disabled:opacity-30"
                        disabled={safePage === resolved.length - 1}
                        onClick={(e) => {
                           e.stopPropagation();
                           setPage((p) => p + 1);
                        }}
                     >
                        <Icon icon="chevron-right" className="text-sm" />
                     </button>
                     <span className="text-[10px] text-gpt-text-secondary">
                        {safePage + 1}/{resolved.length}
                     </span>
                  </span>
               )}
            </span>
         </InlineCitationCardBody>
      </InlineCitationCard>
   );
};

export default CitationGroupPill;
